import { useState, useEffect, useCallback } from 'react';

export type RecentFilter = {
  filterId: string;
  filterType:
    | 'asset-group'
    | 'environment'
    | 'integration'
    | 'integration-group';
};

export interface OmniFilterState {
  assetGroupIds?: string[];
  environmentIds?: string[];
  integrationIds?: string[];
  integrationGroupIds?: string[];
}

const OMNI_FILTER_KEY = 'omni-filter';

const getStoredState = (): OmniFilterState => {
  const stored = localStorage.getItem(OMNI_FILTER_KEY);
  if (stored) {
    try {
      return JSON.parse(stored) as OmniFilterState;
    } catch (error) {
      console.error('Error parsing OmniFilter state:', error);
      return {};
    }
  }
  return {};
};

const getStoredRecentOmniFilters = (): RecentFilter[] => {
  const stored = localStorage.getItem(`${OMNI_FILTER_KEY}-recent-filters`);
  if (stored) {
    try {
      return JSON.parse(stored) as RecentFilter[];
    } catch (error) {
      console.error('Error parsing recent OmniFilters:', error);
      return [];
    }
  }
  return [];
};

/**
 * Hook to manage omni filter state with localStorage persistence
 * Automatically syncs across tabs/windows via storage events
 * @returns [omniFilterState, setOmniFilterState, recentOmniFilters, clearRecentOmniFilters]
 */
export const useOmniFilter = () => {
  const [omniFilterState, setOmniFilterState] = useState<OmniFilterState>(() =>
    getStoredState(),
  );
  const [recentOmniFilters, setRecentOmniFilters] = useState<RecentFilter[]>(
    () => getStoredRecentOmniFilters(),
  );

  useEffect(() => {
    localStorage.setItem(OMNI_FILTER_KEY, JSON.stringify(omniFilterState));
    // Dispatch custom event for same-tab sync (Module Federation)
    window.dispatchEvent(
      new CustomEvent('omni-filter-change', { detail: omniFilterState }),
    );
  }, [omniFilterState]);

  // Listen for storage events to sync across tabs/windows
  // and custom events for same-tab sync (Module Federation)
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === OMNI_FILTER_KEY && e.newValue) {
        try {
          const newState = JSON.parse(e.newValue) as OmniFilterState;
          setOmniFilterState(newState);
        } catch (error) {
          console.error('Error parsing OmniFilter storage event:', error);
        }
      }
    };

    const handleCustomChange = (e: Event) => {
      const customEvent = e as CustomEvent<OmniFilterState>;
      if (customEvent.detail) {
        setOmniFilterState(customEvent.detail);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener(`omni-filter-change`, handleCustomChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener(`omni-filter-change`, handleCustomChange);
    };
  }, []);

  const saveRecentFilter = (state: OmniFilterState) => {
    const newFilters: RecentFilter[] = [];

    // Omni filter only supports single selection
    if (state.assetGroupIds?.[0]) {
      newFilters.push({
        filterId: state.assetGroupIds[0],
        filterType: 'asset-group',
      });
    }
    if (state.environmentIds?.[0]) {
      newFilters.push({
        filterId: state.environmentIds[0],
        filterType: 'environment',
      });
    }
    if (state.integrationIds?.[0]) {
      newFilters.push({
        filterId: state.integrationIds[0],
        filterType: 'integration',
      });
    }
    if (state.integrationGroupIds?.[0]) {
      newFilters.push({
        filterId: state.integrationGroupIds[0],
        filterType: 'integration-group',
      });
    }

    const allFilters = [...newFilters, ...getStoredRecentOmniFilters()];

    // Remove duplicates by inserting into a Map with key "filterType:filterId"
    const recentFiltersMap = new Map<string, RecentFilter>();

    for (const filter of allFilters) {
      const key = `${filter.filterType}:${filter.filterId}`;
      if (!recentFiltersMap.has(key)) {
        recentFiltersMap.set(key, filter);
      }
    }

    const deduplicatedFilters = Array.from(recentFiltersMap.values());

    setRecentOmniFilters(deduplicatedFilters);
    localStorage.setItem(
      `${OMNI_FILTER_KEY}-recent-filters`,
      JSON.stringify(deduplicatedFilters),
    );
  };

  const setFilter = useCallback((state: OmniFilterState) => {
    setOmniFilterState(state);
    saveRecentFilter(state);
  }, []);

  const clearOmniFilter = useCallback(() => {
    setOmniFilterState({});
  }, []);

  const clearRecentOmniFilters = useCallback(() => {
    setRecentOmniFilters([]);
    localStorage.removeItem(`${OMNI_FILTER_KEY}-recent-filters`);
  }, []);

  return {
    clearOmniFilter,
    omniFilterState,
    setOmniFilterState: setFilter, // Should only be used by the OmniFilter!!!
    recentOmniFilters,
    clearRecentOmniFilters,
  };
};
